<template>
    <div>
      <!-- Overlay -->
      <transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-300"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen"
          class="fixed inset-0 bg-black bg-opacity-50 z-50"
          @click="handleCloseModal"
        ></div>
      </transition>

      <!-- Modale slide-out -->
      <transition
        enter-active-class="transition-transform duration-300 ease-out"
        enter-from-class="translate-x-full"
        enter-to-class="translate-x-0"
        leave-active-class="transition-transform duration-300 ease-in"
        leave-from-class="translate-x-0"
        leave-to-class="translate-x-full"
      >
        <div
          v-if="isOpen"
          class="fixed top-0 right-0 h-full w-96 bg-white shadow-xl z-50 overflow-y-auto"
        >
          <!-- Header -->
          <div class="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900">
              {{ t('Informations') }}
            </h2>
            <button
              @click="handleCloseModal"
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="p-6">
            <div
              v-if="modalContent"
              class="prose prose-sm max-w-none"
              v-html="modalContent"
            ></div>
            <div v-else class="text-gray-500 italic">
              {{ t('Aucun contenu configuré') }}
            </div>
          </div>
        </div>
      </transition>
    </div>
  </template>

  <script setup>
  import { ref, computed } from 'vue'
  import t from '../../composables/Translator.js'

  const props = defineProps({
    project: {
      type: Object,
      required: true
    },
    building: {
      type: Object,
      required: false,
      default: null
    }
  })

  const isOpen = ref(false)

  // Exposer la méthode pour ouvrir la modale depuis l'extérieur
  const openModal = () => {
    handleOpenModal()
  }

  // Exposer la méthode pour fermer la modale depuis l'extérieur
  const closeModal = () => {
    handleCloseModal()
  }

  defineExpose({
    openModal,
    closeModal,
    isOpen
  })

  // Computed pour vérifier si la modale est activée
  const isModalEnabled = computed(() => {
    // Priorité aux settings du bâtiment
    if (props.building && props.building.settings && props.building.settings.data) {
      const enabledSetting = props.building.settings.data.find(
        setting => setting.name === 'orbital-modal-enabled'
      )
      if (enabledSetting) {
        return !!Number(enabledSetting.value)
      }
    }

    // Fallback sur les settings du projet
    const enabledSetting = props.project.settings.data.find(
      setting => setting.name === 'orbital-modal-enabled'
    )
    return enabledSetting ? !!Number(enabledSetting.value) : false
  })

  // Computed pour récupérer le contenu de la modale
  const modalContent = computed(() => {
    // Priorité au contenu bâtiment
    if (props.building && props.building.settings && props.building.settings.data) {
      const contentSetting = props.building.settings.data.find(
        setting => setting.name === 'orbital-modal-content'
      )
      if (contentSetting && contentSetting.value !== '') {
        return contentSetting.value
      }
    }

    // Fallback sur le contenu projet
    const contentSetting = props.project.settings.data.find(
      setting => setting.name === 'orbital-modal-content'
    )
    return contentSetting ? contentSetting.value : ''
  })

  const handleOpenModal = () => {
    isOpen.value = true
    // Empêcher le scroll du body
    document.body.style.overflow = 'hidden'
  }

  const handleCloseModal = () => {
    isOpen.value = false
    // Restaurer le scroll du body
    document.body.style.overflow = ''
  }
  </script>

  <style scoped lang="scss">
  /* Styles pour le contenu HTML dans la modale */
  .prose h1, .prose h2, .prose h3 {
    @apply text-gray-900 font-semibold mb-2;
  }

  .prose p {
    @apply text-gray-700 mb-4;
  }

  .prose ul, .prose ol {
    @apply text-gray-700 mb-4;
  }

  .prose a {
    @apply text-gray-600 hover:text-gray-700 underline;
  }
  </style>
